import { Area } from './area';
import { LoadingSpace } from './loading-space';
import { Load } from './load';
import { AreasWithStackingGroup } from '../../core/types';

export function setStackedForVarioFloor(loads: Load[]): void {
  loads.forEach((load: Load): void => {
    load.stacked = false;
  });
}

export function filterStackingGroups(
  areasWithStackingGroups: AreasWithStackingGroup[],
  loadingSpace: LoadingSpace,
  isLoadOnTrailerVarioFloor: boolean,
  isLoadOnSemitrailerVarioFloor: boolean,
): AreasWithStackingGroup[] {
  return areasWithStackingGroups.map((areas: AreasWithStackingGroup) => {
    let filteredAreas: Area[] = [];
    const trailerCenter: number = loadingSpace.width / 2;
    const semiTrailerHalf: number = loadingSpace.width1 / 2;
    const semiTrailerX: number = trailerCenter - semiTrailerHalf;
    const semiTrailerX2: number = trailerCenter + semiTrailerHalf;
    if (isLoadOnTrailerVarioFloor) {
      filteredAreas = areas.areas.filter((area: Area): boolean => {
        return (
          area.x >= 0 &&
          area.x2() <= loadingSpace.width &&
          area.y >= loadingSpace.varioTrailerFloorHeight &&
          area.z >= 0 &&
          area.z2() <= loadingSpace.length
        );
      });
    }
    if (isLoadOnSemitrailerVarioFloor) {
      filteredAreas = areas.areas.filter((area: Area): boolean => {
        return (
          area.x >= semiTrailerX &&
          area.x2() <= semiTrailerX2 &&
          area.y >= loadingSpace.varioTrailerFloorHeight &&
          area.z >= 0 &&
          area.z2() <= loadingSpace.length
        );
      });
    }
    if (!isLoadOnTrailerVarioFloor && !isLoadOnSemitrailerVarioFloor) {
      filteredAreas = areas.areas.filter((area: Area): boolean => {
        return (
          (area.x >= 0 &&
            area.x2() <= loadingSpace.width &&
            area.y < loadingSpace.varioTrailerFloorHeight &&
            area.z >= 0 &&
            area.z2() <= loadingSpace.length) ||
          (area.x >= semiTrailerX &&
            area.x2() <= semiTrailerX2 &&
            area.y < loadingSpace.varioTrailerFloorHeight &&
            area.z >= 0 &&
            area.z2() <= loadingSpace.length)
        );
      });
    }
    const area = {
      id: areas.id,
      areas: filteredAreas,
    };
    return area;
  });
}

export function filterVarioAreas(
  areas: Area[],
  loadingSpace: LoadingSpace,
  isLoadOnTrailerVarioFloor: boolean,
  isLoadOnSemitrailerVarioFloor: boolean,
): Area[] {
  const varioFloorHeight: number = loadingSpace.varioTrailerFloorHeight;
  const semiTrailerFloorHeight: number = loadingSpace.varioSemiTrailerFloorHeight;
  let filteredAreas: Area[] = [];
  if (isLoadOnTrailerVarioFloor) {
    filteredAreas = areas.filter((area: Area): boolean => {
      const isVarioFloor: boolean =
        area.y >= varioFloorHeight &&
        area.z2() <= loadingSpace.length &&
        area.z >= 0 &&
        area.x >= 0 &&
        area.x2() <= loadingSpace.width;
      return isVarioFloor;
    });
  }
  if (isLoadOnSemitrailerVarioFloor) {
    filteredAreas = areas.filter((area: Area): boolean => {
      const trailerCenter: number = loadingSpace.width / 2;
      const semiTrailerHalf: number = loadingSpace.width1 / 2;
      const semiTrailerX: number = trailerCenter - semiTrailerHalf;
      const semiTrailerX2: number = trailerCenter + semiTrailerHalf;
      const isSemiVarioFloor: boolean =
        area.y >= semiTrailerFloorHeight &&
        area.x <= semiTrailerX &&
        area.x2() <= semiTrailerX2 &&
        area.z2() <= loadingSpace.length + loadingSpace.gap + loadingSpace.length1 &&
        area.z >= loadingSpace.length + loadingSpace.gap;
      return isSemiVarioFloor;
    });
  }
  if (!isLoadOnTrailerVarioFloor && !isLoadOnSemitrailerVarioFloor) {
    filteredAreas = areas
      .filter((area: Area): boolean => {
        const isVarioFloor: boolean =
          area.y >= varioFloorHeight &&
          area.z2() <= loadingSpace.length &&
          area.z >= 0 &&
          area.x >= 0 &&
          area.x2() <= loadingSpace.width;
        return !isVarioFloor;
      })
      .filter((area: Area): boolean => {
        const trailerCenter: number = loadingSpace.width / 2;
        const semiTrailerHalf: number = loadingSpace.width1 / 2;
        const semiTrailerX: number = trailerCenter - semiTrailerHalf;
        const semiTrailerX2: number = trailerCenter + semiTrailerHalf;
        const isSemiVarioFloor: boolean =
          area.y >= semiTrailerFloorHeight &&
          area.z2() <= loadingSpace.length + loadingSpace.gap + loadingSpace.length1 &&
          area.z >= loadingSpace.length + loadingSpace.gap &&
          area.x >= semiTrailerX &&
          area.x2() <= semiTrailerX2;
        return !isSemiVarioFloor;
      });
  }
  return filteredAreas;
}

export function isLoadOnTrailer(load: Load, loadingSpace: LoadingSpace): boolean {
  return (
    load.x >= 0 &&
    load.x2() <= loadingSpace.width &&
    load.z >= 0 &&
    load.z2() <= loadingSpace.length
  );
}

export function isLoadOnSemiTrailer(load: Load, loadingSpace: LoadingSpace): boolean {
  const trailerCenter: number = loadingSpace.width / 2;
  const semiTrailerHalf: number = loadingSpace.width1 / 2;
  const semiTrailerX: number = trailerCenter - semiTrailerHalf;
  const semiTrailerX2: number = trailerCenter + semiTrailerHalf;
  return (
    load.x >= semiTrailerX &&
    load.x2() <= semiTrailerX2 &&
    load.z >= loadingSpace.length + loadingSpace.gap &&
    load.z2() <= loadingSpace.length + loadingSpace.gap + loadingSpace.length1
  );
}

export function calculateVarioAreaHeight(load: Load, loadingSpace: LoadingSpace): number {
  let areaHeight: number;
  const isLoadOnSemiTrailer2: boolean = isLoadOnSemiTrailer(load, loadingSpace);
  const isLoadOnTrailer2: boolean = isLoadOnTrailer(load, loadingSpace);
  const varioTrailerFloorBottom: number =
    loadingSpace.varioTrailerFloorHeight - loadingSpace.varioTrailerFloorThickness;
  const varioSemiTrailerFloorBottom: number =
    loadingSpace.varioSemiTrailerFloorHeight - loadingSpace.varioTrailerFloorThickness;
  const loadTop: number = load.position.y + load.height;
  const isLoadUnderTrailerVario: boolean = loadTop <= varioTrailerFloorBottom;
  const isLoadUnderSemiTrailerVario: boolean = loadTop <= varioSemiTrailerFloorBottom;
  if (isLoadOnTrailer2 && isLoadUnderTrailerVario) {
    areaHeight = varioTrailerFloorBottom - loadTop;
  } else if (isLoadOnTrailer2) {
    areaHeight = loadingSpace.height - loadTop;
  } else if (isLoadOnSemiTrailer2 && isLoadUnderSemiTrailerVario) {
    areaHeight = varioSemiTrailerFloorBottom - loadTop;
  } else if (isLoadOnSemiTrailer2) {
    areaHeight = loadingSpace.height - loadTop;
  }
  return areaHeight;
}
